import React, {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Button, Form, Input, message, Modal, Tooltip} from "antd";
import {DeleteOutlined, DownloadOutlined, EditOutlined, PlusOutlined, UploadOutlined} from "@ant-design/icons";
import VenueService from "../services/venue.service";
import TrackService from "../services/track.service";

const {confirm} = Modal;

const CODE_PATTERN = /^[a-zA-Z0-9-_]{1,15}$/;
const LABEL_PATTERN = /^[a-z0-9À-ÿ _\-']{1,50}$/i;

const styleHead = {
    backgroundColor: '#E3E9EE',
    border: '0 solid #DDDDDD',
}

const styleLineEven = {
    backgroundColor: '#FFFFFF',
    border: '0 solid #DDDDDD',
}

const styleLineOdd = {
    backgroundColor: '#F0F0F0',
    border: '0 solid #DDDDDD',
}

const styleCellCode = {
    textAlign: 'center',
    width: 75,
}

const styleCellLabel = {
    textAlign: 'left',
    minWidth: 200,
}

const styleCellNbTracks = {
    textAlign: 'right',
    minWidth: 75,
    paddingRight: 10,
}

const styleCellActions = {
    textAlign: 'center',
    minWidth: 90,
    whiteSpace: 'nowrap',
}

// base.css forces height/radius on .ant-btn:focus, so keep the small circle size explicitly
const styleActionButton = {
    height: 24,
    minWidth: 24,
    borderRadius: '50%',
}

function VenueDataRow({venue, index, isAdmin, onEdit, onDelete}) {
    const {t} = useTranslation();
    return (
        <tr style={index%2===0 ? styleLineEven : styleLineOdd}>
            <td style={styleCellCode}>{venue.code}</td>
            <td style={styleCellLabel}>{venue.label}</td>
            <td style={styleCellNbTracks}>{venue.nbTracks || 0}</td>
            { isAdmin &&
                <td style={styleCellActions}>
                    <Tooltip title={t('venue.edit')}>
                        <Button onClick={() => onEdit(venue)} shape="circle" size="small" style={styleActionButton}
                                icon={<EditOutlined/>}/>
                    </Tooltip>
                    &nbsp;
                    <Tooltip title={venue.nbTracks ? t('venue.deleteForbiddenTracks') : t('venue.delete')}>
                        <Button onClick={() => onDelete(venue)} shape="circle" size="small" style={styleActionButton} danger
                                disabled={!!venue.nbTracks} icon={<DeleteOutlined/>}/>
                    </Tooltip>
                </td>
            }
        </tr>
    )
}

export default function VenueDataList() {
    const {t} = useTranslation();
    const [form] = Form.useForm();

    const [venues, setVenues] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editedVenue, setEditedVenue] = useState(null); // null => creation
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    const load = useCallback(() => {
        return Promise.all([VenueService.listAll(), TrackService.listAll()]).then(([venues, tracks]) => {
            tracks.forEach(track => {
                const venue = venues.find(v => v.code === track.venueCode);
                if (venue) {
                    venue.nbTracks = (venue.nbTracks || 0) + 1;
                }
            });
            setVenues(venues.sort((v1, v2) => v1.label < v2.label ? -1 : 1));
        });
    }, []);

    useEffect(() => {
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            setIsAdmin(!!user?.roles?.includes("admin"));
        } catch (e) {
            setIsAdmin(false);
        }
        load();
    }, [load]);

    // create / edit --------------------------------------------------
    const openCreate = () => {
        setEditedVenue(null);
        form.resetFields();
        setModalOpen(true);
    };

    const openEdit = (venue) => {
        setEditedVenue(venue);
        form.resetFields();
        form.setFieldsValue({
            code: venue.code,
            label: venue.label,
            city: venue.city,
            address: venue.address,
        });
        setModalOpen(true);
    };

    const submit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (e) {
            return; // the form displays the validation errors
        }
        setSaving(true);
        try {
            if (editedVenue) {
                const {label, city, address} = values;
                await VenueService.update(editedVenue.uuid, {label, city: city || "", address});
                message.success(t('venue.updated'));
            } else {
                await VenueService.create({...values, city: values.city || ""});
                message.success(t('venue.created'));
            }
            setModalOpen(false);
            await load();
        } catch (error) {
            message.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    // delete ---------------------------------------------------------
    const del = (venue) => {
        confirm({
            title: t('venue.confirmDelete'),
            content: `${venue.label} (${venue.code})`,
            okType: 'danger',
            onOk: async () => {
                try {
                    await VenueService.remove(venue.uuid);
                    message.success(t('venue.deleted'));
                    await load();
                } catch (error) {
                    message.error(error.message);
                }
            },
        });
    };

    // export / import ------------------------------------------------
    const exportVenues = () => {
        const content = venues.map(({code, code2, label, city, address}) => ({code, code2, label, city, address}));
        const blob = new Blob([JSON.stringify(content, null, 2)], {type: 'application/json;charset=utf-8'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'venues_' + new Date().toISOString().slice(0, 10) + '.json';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const importFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ''; // allows to re-import the same file
        if (!file) return;

        let list;
        try {
            const parsed = JSON.parse(await file.text());
            list = Array.isArray(parsed) ? parsed : parsed?.venues;
            if (!Array.isArray(list) || !list.length) throw new Error();
        } catch (error) {
            message.error(t('venue.importBadFile'));
            return;
        }

        setImporting(true);
        try {
            const result = await VenueService.importVenues(list);
            Modal.info({
                title: t('venue.importDone'),
                content: (
                    <div>
                        <div>{t('venue.importCreated')} : {result.created.length}{result.created.length ? ' (' + result.created.join(', ') + ')' : ''}</div>
                        <div>{t('venue.importSkipped')} : {result.skipped.length}{result.skipped.length ? ' (' + result.skipped.join(', ') + ')' : ''}</div>
                        { result.invalid.length > 0 &&
                            <div>{t('venue.importInvalid')} : {result.invalid.length}
                                <ul>{result.invalid.map(i => <li key={i.index}>#{i.index + 1} {i.code || '?'} : {i.reason}</li>)}</ul>
                            </div>
                        }
                    </div>
                ),
            });
            await load();
        } catch (error) {
            message.error(error.message);
        } finally {
            setImporting(false);
        }
    };

    // render ---------------------------------------------------------
    return (
        <div>
            { isAdmin &&
                <div style={{marginBottom: 10, display: 'flex', gap: 10}}>
                    <Button type="primary" icon={<PlusOutlined/>} onClick={openCreate}>{t('venue.add')}</Button>
                    <Button icon={<DownloadOutlined/>} onClick={exportVenues} disabled={!venues.length}>{t('venue.export')}</Button>
                    <Button icon={<UploadOutlined/>} onClick={() => fileInputRef.current?.click()} loading={importing}>{t('venue.import')}</Button>
                    <input ref={fileInputRef} type="file" accept=".json,application/json" style={{display: 'none'}} onChange={importFile}/>
                </div>
            }
            <div style={{marginBottom: 10}}>{t('venue.count', {count: venues.length})}</div>
            <table className="venue_list">
                <thead>
                    <tr>
                        <th style={styleHead}>{t("venue.table.code")}</th>
                        <th style={styleHead}>{t("venue.table.label")}</th>
                        <th style={styleHead}>{t("venue.table.nbTracks")}</th>
                        { isAdmin && <th style={styleHead}>{t("venue.table.actions")}</th> }
                    </tr>
                </thead>
                <tbody>
                    { venues.map((v, i) =>
                        <VenueDataRow key={v.uuid || v.code} venue={v} index={i} isAdmin={isAdmin} onEdit={openEdit} onDelete={del} />
                    ) }
                </tbody>
            </table>

            <Modal
                title={editedVenue ? t('venue.edit') : t('venue.add')}
                open={modalOpen}
                onOk={submit}
                onCancel={() => setModalOpen(false)}
                confirmLoading={saving}
                okText={t('venue.save')}
                cancelText={t('venue.cancel')}
                destroyOnClose
            >
                <Form form={form} layout="vertical" preserve={false}>
                    <Form.Item name="code" label={t('venue.table.code')}
                               rules={[{required: true, message: t('venue.form.required')},
                                       {pattern: CODE_PATTERN, message: t('venue.form.codeFormat')}]}>
                        <Input maxLength={15} disabled={!!editedVenue}/>
                    </Form.Item>
                    <Form.Item name="label" label={t('venue.table.label')}
                               rules={[{required: true, message: t('venue.form.required')},
                                       {pattern: LABEL_PATTERN, message: t('venue.form.labelFormat')}]}>
                        <Input maxLength={50}/>
                    </Form.Item>
                    <Form.Item name="city" label={t('venue.table.city')}
                               rules={[{pattern: LABEL_PATTERN, message: t('venue.form.labelFormat')}]}>
                        <Input maxLength={50}/>
                    </Form.Item>
                    <Form.Item name="address" label={t('venue.table.address')}>
                        <Input.TextArea rows={2} maxLength={500}/>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}
